import { Checkbox, Modal, Typography } from "antd";
import React from "react";

export interface IRunDeduplicationOptions {
    // Retire now the duplicates in the golden dataset flagged as subsumed by a golden survivor
    // (PRE-2837); those sent for review are never retired by a run.
    golden: boolean;
    // Retire the duplicates merged from the other datasets.
    other: boolean;
    // Keep a record of each resolved cluster (ARCHIVE, the default), or none (DISCARD). PRE-2797.
    archive: boolean;
}

const DEFAULT_OPTIONS: IRunDeduplicationOptions = { golden: false, other: false, archive: true };

interface IRunDeduplicationModalProps {
    open: boolean;
    onRun: (options: IRunDeduplicationOptions) => void;
    onCancel: () => void;
}

// Run Deduplication for a tenant with Golden Nominal maintenance on: whether the run retires the
// duplicates it merges, from the golden datasets and from the other datasets. Both are off by
// default, so a run keeps every duplicate unless asked (PM decision of 2026-09-30). And, separately,
// whether it keeps a record of what it decided for each cluster (on by default; PRE-2797).
const RunDeduplicationModal = ({ open, onRun, onCancel }: IRunDeduplicationModalProps) => {
    const [options, setOptions] = React.useState<IRunDeduplicationOptions>(DEFAULT_OPTIONS);

    // Unchecked each time it opens.
    React.useEffect(() => {
        if (open) {
            setOptions(DEFAULT_OPTIONS);
        }
    }, [open]);

    return (
        <Modal open={open} title="Run deduplication" okText="Run deduplication" onOk={() => onRun(options)} onCancel={onCancel} destroyOnHidden data-testid="run-deduplication-modal">
            <div className="flex flex-col gap-3">
                <Typography.Paragraph type="secondary" className="!mb-0">
                    Only golden records are changed. A cluster with a golden record takes in its duplicates&apos; values; a cluster without one is left as it is,
                    unless its search creates golden records, in which case one is created from it. Merged duplicates are kept unless you choose to delete them
                    below; clusters nominated to delete their duplicates still delete them (golden ones only when chosen). A deleted duplicate is retired: it is
                    removed from its dataset, and remembers which record it was merged into.
                </Typography.Paragraph>
                <Typography.Paragraph type="secondary" className="!mb-0">
                    A duplicate in the golden dataset is flagged as subsumed when its golden record now holds everything it held; otherwise it is sent to the
                    review queue. Flagged duplicates are left out of later duplicate searches, and can be retired now or later from the review queue&apos;s
                    Subsumed records.
                </Typography.Paragraph>
                <Checkbox
                    checked={options.golden}
                    onChange={(event) => setOptions({ ...options, golden: event.target.checked })}
                    data-testid="run-deduplication-delete-golden"
                >
                    Retire the flagged duplicates in the golden dataset now
                </Checkbox>
                <Checkbox
                    checked={options.other}
                    onChange={(event) => setOptions({ ...options, other: event.target.checked })}
                    data-testid="run-deduplication-delete-other"
                >
                    Delete duplicates from the other datasets
                </Checkbox>
                <Typography.Text type="secondary" className="text-xs">
                    Deleting suits transient feeds that are not systems of record: their contribution stays in the golden records, and a record that comes back
                    later is matched to the same golden record.
                </Typography.Text>
                <Checkbox
                    checked={options.archive}
                    onChange={(event) => setOptions({ ...options, archive: event.target.checked })}
                    data-testid="run-deduplication-archive"
                >
                    Keep a record of what was decided for each cluster
                </Checkbox>
                <Typography.Text type="secondary">A golden record is never merged into a record that is not golden without a steward's review.</Typography.Text>
            </div>
        </Modal>
    );
};

export default RunDeduplicationModal;
